import { Link } from "react-router-dom";
import { ArrowRight, Phone, CalendarCheck } from "lucide-react";
import { BRAND, SERVICES } from "../lib/constants";
import HeroShield from "../components/HeroShield";

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--isa-navy)] text-white">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${BRAND.images.servicesTexture}')` }}
        />
        <div className="absolute inset-0 bg-[color:var(--isa-navy)]/85" />
        <HeroShield position="right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <span className="eyebrow text-[color:var(--isa-gold)]">Our Services</span>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Insurance &amp; financial solutions for <em className="not-italic text-[color:var(--isa-gold)]">every stage</em>.
          </h1>
          <p className="mt-7 text-base lg:text-lg font-sans text-white/80 max-w-3xl leading-relaxed">
            From your first apartment to your retirement, your growing family to your growing business —
            we offer a complete range of coverage and planning options tailored to your needs.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/contact?form=quote" className="btn-primary" data-testid="services-hero-quote-button">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${BRAND.phoneTel}`} className="btn-outline-gold" data-testid="services-hero-call-button">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.slug}
                  className="bg-white p-10 card-lift relative"
                  data-testid={`service-detail-${s.slug}`}
                >
                  <div className="w-14 h-14 inline-flex items-center justify-center border border-[color:var(--isa-gold)] text-[color:var(--isa-gold)] mb-6">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl text-[color:var(--isa-navy)] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm font-sans text-gray-600 leading-relaxed">
                    {s.description}
                  </p>
                  <Link
                    to="/contact?form=quote"
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-[0.18em] text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
                  >
                    Get a Free Quote <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-32 bg-[color:var(--isa-soft-gray)]" data-testid="services-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">How We Work</span>
            <h2 className="mt-5 section-heading text-4xl lg:text-5xl">
              A simple, <em className="not-italic text-[color:var(--isa-gold)]">no-pressure</em> process.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200">
            {[
              { step: "01", title: "Listen", body: "We start with your story — what you have, what you want to protect, and where you're headed." },
              { step: "02", title: "Compare", body: "As independent advisors, we shop multiple A-rated carriers to match coverage to your goals and budget." },
              { step: "03", title: "Guide", body: "You choose the right coverage with confidence. We stay with you for renewals, claims, and life's changes." },
            ].map((s, i) => (
              <div key={s.step} className="bg-white p-10 card-lift" data-testid={`how-step-${i}`}>
                <p className="font-serif text-5xl text-[color:var(--isa-gold)]/30">{s.step}</p>
                <h3 className="mt-2 font-serif text-2xl text-[color:var(--isa-navy)]">{s.title}</h3>
                <p className="mt-3 text-sm font-sans text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[color:var(--isa-navy)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <span className="eyebrow text-[color:var(--isa-gold)]">Take The Next Step</span>
            <h2 className="mt-4 font-serif text-3xl lg:text-5xl leading-tight">
              Free quotes, friendly advice — <em className="not-italic text-[color:var(--isa-gold)]">always</em>.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact?form=quote" className="btn-primary" data-testid="services-cta-quote">
              Request a Quote
            </Link>
            <Link to="/contact?form=consult" className="btn-outline-gold" data-testid="services-cta-consult">
              <CalendarCheck className="w-4 h-4" /> Book a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
